/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import {
	CartService,
	ICartItem,
	IDates,
	IOfferContext,
	IVignetteOffer,
	OfferContext,
	VignetteContext,
	convertPlatesToArray,
	convertVignetteIDateToDates,
	formatPlates,
	formatPlatesPayload,
	getErrorTextFromResponse,
	getFuelType,
	getVignetteByCatalogAndFuel,
	getVignetteDates,
	updateFuelType,
	IPlatesValidatedState,
	ITypeVignetteOrderingProcessFields,
	setRouteLangParam,
	IDeleteProductOption,
	IHandleCartErrorCodeProps,
	IHandleCartItemUpdateProps,
	IProductSummary,
	IProductSummaryVariants,
	ITollSectionSummary,
	IVignetteSummmary,
	getLabelValue,
	formatTripsAmountLabel,
	formatDateTimeValues,
	getUrlBasePath,
} from '@adac-mp/shared';
import { redirectToErrorPage } from '@adac-mp/shared/src/services/axios-config';
import {
	DeleteIcon,
	ErrorCrossIcon,
	Toast,
	Typography,
	CartAndSystemValidationModal,
	EnterPlates,
	StartDatePicker,
	TollSectionConfiguration,
	VignetteRadioButtons,
	VignetteFuelType,
} from '../../index';
import { renderProductSummaryElement } from '../../utils';
import { gatherProductTrackingData } from '../../utils/tracking/tracking';

const handleCartErrorCode = ({
	navigate,
	res,
	currProduct,
	labels,
	lang,
	updateSelectedCartItem,
	setApiError,
	setErrorNumber = () => {},
	setValModalIsOpen = () => {},
}: IHandleCartErrorCodeProps) => {
	const isFlexProduct = currProduct?.offer.moduleTypeId === 4;
	if (res.errors[0].errorNumber === 61 && !isFlexProduct) {
		setErrorNumber(res.errors[0].errorNumber);
		setValModalIsOpen(true);
		updateSelectedCartItem(currProduct);
		return;
	}
	if (res.code === 401) {
		navigate(getUrlBasePath(`/session-expired${setRouteLangParam(lang)}`));
		updateSelectedCartItem(undefined);
	} else if (res.redirectToErrorPage) {
		redirectToErrorPage(res.errors);
		updateSelectedCartItem(undefined);
	} else {
		updateSelectedCartItem(currProduct);
		const errorText = getErrorTextFromResponse(res.errors, getLabelValue('mp.generalError', labels));
		setApiError(errorText);
	}
};

const handleCartItemUpdate = ({
	navigate,
	updatedCartItem,
	currProduct,
	useracceptance,
	lang,
	labels,
	updateCart,
	updateSelectedCartItem,
	setIsLoading,
	setApiError,
	setErrorNumber,
	setValModalIsOpen,
}: IHandleCartItemUpdateProps) => {
	const cartHash = sessionStorage.getItem('cartHash') || '';
	const cartToken = sessionStorage.getItem('cartToken') || '';
	const customerHash = sessionStorage.getItem('customerHash') || '';
	const { uuid, offerHash, licenseplate, countryCode, checkInDateTime, checkOutDateTime, licenseplateType } =
		updatedCartItem;
	const platesPayload = formatPlatesPayload(licenseplate);
	const body = {
		offerHash,
		licenseplate: platesPayload,
		licenseplateType,
		countryCode,
		checkInDateTime,
		checkOutDateTime,
		customerHash,
		cartHash,
		uuid,
		useracceptance,
	};
	setIsLoading(true);
	CartService.updateCartItem({ body, token: cartToken, lang }).then((res) => {
		if (res.code === 200 && res.cart) {
			updateCart(res.cart);
			const cartDetails = res.cart.cartDetails;
			const offers = cartDetails.map((item) => item.offer);
			const lastUpdatedCartItem = cartDetails[cartDetails.length - 1];
			updateSelectedCartItem(lastUpdatedCartItem);
			gatherProductTrackingData({ offers, status: 'update', updatedPrice: res.cart.price.amount });
		} else {
			handleCartErrorCode({
				navigate,
				res,
				currProduct,
				labels,
				lang,
				updateSelectedCartItem,
				setApiError,
				setErrorNumber,
				setValModalIsOpen,
			});
		}
		setIsLoading(false);
	});
};

const deleteProductOption = ({
	navigate,
	uuid,
	offer,
	cartLabels,
	labels,
	lang,
	updateCart,
	redirectToEmptyCart,
	setApiError,
	setIsLoading,
	updateSelectedCartItem,
}: IDeleteProductOption): IProductSummary => {
	const renderDeleteProductBtn = () => (
		<span className="w-full flex gap-1.5 justify-end">
			<DeleteIcon bold size="20" />
			<Typography variant="title">{cartLabels?.deleteButtonLabel || 'Dieses Produkt löschen'}</Typography>
		</span>
	);
	return {
		id: 'delete product',
		type: 'cart',
		label: cartLabels?.deleteOverlayLabel || 'Artikel löschen',
		value: renderDeleteProductBtn(),
		component: (
			<Typography component="p" className="px-3 pt-1.5 pb-3" size="lg" color="title">
				{cartLabels?.deleteOverlayText || 'Wollen Sie diesen Artikel wirklich löschen?'}
			</Typography>
		),
		submitButtonLabel: cartLabels?.deleteOverlaySubmit || 'Artikel aus dem Warenkorb löschen',
		cancelButtonLabel: cartLabels?.deleteOverlayCancel || 'Nein, Artikel behalten',
		tracking: {
			label: `${offer.serviceCatalogName} löschen`,
			element: `Warenkorb | Produktkachel`,
			category: 'Content',
		},
		handleChange: () => {
			const cartHash = sessionStorage.getItem('cartHash');
			const cartToken = sessionStorage.getItem('cartToken');
			if (cartHash && cartToken) {
				setIsLoading(true);
				CartService.removeCartItem({
					body: {
						cartHash,
						uuid,
					},
					token: cartToken,
					lang,
				}).then((res) => {
					const sourcePage =
						window.location.pathname.includes('checkout') ||
						window.location.pathname.includes('product-done');
					if (res.code === 200 && res.cart) {
						gatherProductTrackingData({ offers: [offer], status: 'remove' });
						if (res.cart.cartDetails.length === 0 && sourcePage) {
							redirectToEmptyCart();
						}
						updateCart(res.cart);
						const cartDetails = res.cart.cartDetails;
						const updatedCartItem = cartDetails[cartDetails.length - 1];
						updateSelectedCartItem(updatedCartItem);
					} else {
						handleCartErrorCode({ res, navigate, labels, lang, updateSelectedCartItem, setApiError });
					}
					setIsLoading(false);
				});
			}
		},
		handleClose: () => {},
	};
};

export const VignetteProductSummary = ({
	currProduct,
	hasCategories,
	hasSpecialPlates = false,
	hasFuelType,
	multipleProducts,
	cartLabels,
	redirectToEmptyCart,
	setIsLoading,
}: IVignetteSummmary) => {
	const navigate = useNavigate();
	const { offer, licenseplate, checkOutDateTime, checkInDateTime, uuid, offerHash, countryCode, licenseplateType } =
		currProduct;
	const { supplierId } = offer;
	const { selectedCartItem, updateSelectedCartItem, updateCart } = useContext(VignetteContext);
	const { vignetteOffers, labels, lang } = useContext<IOfferContext>(OfferContext);
	const [hideModal, setHideModal] = useState<boolean>(false);
	const [valModalIsOpen, setValModalIsOpen] = useState(false);
	const [errorNumber, setErrorNumber] = useState<number | undefined>(undefined);
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});
	const [updatedVignette, setUpdatedVignette] = useState<IVignetteOffer | undefined>({
		...offer,
		offerHash,
	} as IVignetteOffer);
	const [vignetteDates, setVignetteDates] = useState<IDates | undefined>(
		convertVignetteIDateToDates(checkInDateTime, checkOutDateTime),
	);

	const [updatedFuelId, setUpdatedFuelId] = useState(offer?.fuelTypeId);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const vignetteIsEditable =
		vignetteOffers.filter(
			(vignette) =>
				vignette.moduleDetailId === offer.moduleDetailId && vignette.servcatSeason === offer.servcatSeason,
		).length > 1;

	useEffect(() => {
		setUpdatedLicencePlate({
			value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
			error: false,
			confirmed: false,
			countryCode,
			specialPlates: Boolean(licenseplateType),
		});
		setVignetteDates(convertVignetteIDateToDates(checkInDateTime, checkOutDateTime));
	}, [currProduct]);

	useEffect(() => {
		if (updatedVignette && hasFuelType) {
			const currentVignette = getVignetteByCatalogAndFuel(
				vignetteOffers,
				String(updatedVignette?.serviceCatalogCode),
				updatedFuelId,
			);
			if (currentVignette) {
				setUpdatedVignette(currentVignette);
			}
		}
	}, [updatedFuelId]);

	const prepareVignettePayload = (updatedProduct: ICartItem, useracceptance?: boolean, newDates?: IDates) => {
		const newProduct = updatedProduct;
		if (newDates) {
			const { checkInDateTime: updatedCheckinDateTime, checkOutDateTime: updatedCheckoutDateTime } =
				getVignetteDates(newDates);
			if (updatedCheckinDateTime) newProduct.checkInDateTime = updatedCheckinDateTime;
			newProduct.checkOutDateTime = updatedCheckoutDateTime;
		}

		handleCartItemUpdate({
			updatedCartItem: newProduct,
			currProduct,
			navigate,
			useracceptance,
			lang,
			updateCart,
			updateSelectedCartItem,
			setIsLoading,
			setApiError,
			setErrorNumber,
			setValModalIsOpen,
		});
	};

	const productSummary: IProductSummary[] = [
		{
			id: 'product',
			type: vignetteIsEditable ? 'edit' : undefined,
			label: getLabelValue('mp.article', labels) || 'Artikel',
			value: currProduct ? offer?.serviceCatalogName.trim() : <Skeleton inline height="100%" width="100%" />,
			editable: supplierId !== 14,
			tracking: {
				label: 'Artikel bearbeiten',
				element: 'Warenkorb | Produktkachel',
				category: 'Content',
			},
			component: (
				<div className="mx-2">
					<VignetteRadioButtons
						dates={vignetteDates}
						defaultVignetteId={offer?.serviceCatalogCode}
						defaultCategoryId={hasCategories ? offer?.moduleDetailId : undefined}
						selectedFuelId={Number(offer?.fuelTypeId)}
						moduleTypeIdFilter={2} // Displaying only Vignette types for editing
						supplierId={offer?.supplierId}
						onVignetteChanged={(changedVignette) => {
							setUpdatedVignette(changedVignette);
						}}
					/>
				</div>
			),
			handleChange: () => {
				if (updatedVignette) {
					prepareVignettePayload(
						{
							...currProduct,
							offerHash: updatedVignette.offerHash,
						},
						true,
						updatedVignette.dates,
					);
				}
			},
		},

		{
			id: 'dates',
			type: 'edit',
			label: getLabelValue('mp.valid', labels) || 'Gültig',
			tracking: {
				label: 'Gültigkeit bearbeiten',
				element: 'Warenkorb | Produktkachel',
				category: 'Content',
			},
			value: offer ? (
				formatDateTimeValues(checkOutDateTime, checkInDateTime, lang, undefined, labels)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
			editable: supplierId !== 14,
			component: (
				<StartDatePicker
					inline
					dates={vignetteDates}
					offer={updatedVignette}
					onStartDateChange={(changedVignette, dates) => {
						setUpdatedVignette(changedVignette as IVignetteOffer);
						setVignetteDates(dates);
					}}
				/>
			),
			handleChange: () => {
				if (updatedVignette) {
					prepareVignettePayload(
						{
							...currProduct,
							offerHash: updatedVignette.offerHash,
						},
						true,
						vignetteDates,
					);
				}
			},
			handleClose: () => {
				setUpdatedVignette({
					...offer,
					offerHash,
				} as IVignetteOffer);
				setVignetteDates(convertVignetteIDateToDates(checkInDateTime, checkOutDateTime));
			},
		},
		{
			id: 'licenseplate',
			type: 'edit',
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			tracking: {
				label: 'Kennzeichen bearbeiten',
				element: `Warenkorb | Produktkachel`,
				category: 'Content',
			},
			value: offer ? formatPlates(licenseplate) : <Skeleton inline height="100%" width="100%" />,
			component: (
				<EnterPlates
					withAutoFocus
					border
					insideOfModal
					hasSpecialPlates={hasSpecialPlates}
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={offer.customerCountries}
					setHideModal={setHideModal}
				/>
			),
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hideModal,
			handleChange: () => {
				if (!updatedLicencePlate.confirmed) return;
				if (Array.isArray(updatedLicencePlate.value)) {
					prepareVignettePayload(
						{
							...currProduct,
							licenseplate: String(updatedLicencePlate.value),
							countryCode: updatedLicencePlate.countryCode,
							licenseplateType: Number(updatedLicencePlate.specialPlates),
						},
						true,
					);
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
	];

	if (hasFuelType) {
		productSummary.push({
			id: 'fuel',
			type: 'edit',
			label: 'Antrieb',
			value: offer ? getFuelType(offer) : <Skeleton inline height="100%" width="100%" />,
			component: (
				<VignetteFuelType
					withAllFuelsOption
					className="mx-2"
					defaultFuelId={offer?.fuelTypeId}
					onFuelChanged={setUpdatedFuelId}
				/>
			),
			handleChange: () => {
				const updatedProduct = updateFuelType(
					updateSelectedCartItem,
					vignetteOffers,
					updatedFuelId,
					selectedCartItem,
				);
				if (updatedProduct) {
					prepareVignettePayload(
						{
							...currProduct,
							offerHash: updatedProduct?.offerHash,
						},
						true,
					);
				}
			},
		});
	}

	if (multipleProducts) {
		productSummary.push(
			deleteProductOption({
				uuid,
				offer,
				navigate,
				cartLabels,
				lang,
				labels,
				updateCart,
				redirectToEmptyCart,
				setApiError,
				updateSelectedCartItem,
				setIsLoading,
			}),
		);
	}

	return (
		<>
			{renderProductSummaryElement(productSummary, offer)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
			{errorNumber && (
				<CartAndSystemValidationModal
					isEditing
					errorCode={errorNumber}
					isOpen={valModalIsOpen}
					onSubmit={() => {
						if (Array.isArray(updatedLicencePlate.value)) {
							prepareVignettePayload(
								{
									...currProduct,
									offerHash: updatedVignette ? updatedVignette.offerHash : currProduct.offerHash,
									licenseplate: String(updatedLicencePlate.value),
									countryCode: updatedLicencePlate.countryCode,
									licenseplateType: Number(updatedLicencePlate.specialPlates),
								},
								false,
								vignetteDates,
							);
						}
					}}
					onClose={() => setValModalIsOpen(false)}
				/>
			)}
		</>
	);
};

export const TollRouteProductSummary = ({
	currProduct,
	hasSpecialPlates = false,
	cartLabels,
	redirectToEmptyCart,
	setIsLoading,
}: ITollSectionSummary) => {
	const navigate = useNavigate();
	const { tollSectionOffers, labels, lang } = useContext(OfferContext);
	const { contentfulLabels, updateCart, updateSelectedCartItem } = useContext(VignetteContext);
	const { offer, licenseplate, checkInDateTime, checkOutDateTime, uuid, countryCode, licenseplateType } = currProduct;
	const [updatedProductCode, setUpdatedProductCode] = useState(offer?.serviceCatalogCode ?? '');
	const [hasError, setHasError] = useState(false);
	const [hiddenModal, setHideModal] = useState<boolean>(false);
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});
	const correspondingTollSections = tollSectionOffers.filter(
		(currOffer) => currOffer.serviceCatalogName.slice(0, 10) === offer?.serviceCatalogName.slice(0, 10),
	);
	const [apiError, setApiError] = useState<string | undefined>(undefined);

	useEffect(() => {
		setUpdatedLicencePlate({
			value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
			error: false,
			confirmed: false,
			countryCode,
			specialPlates: Boolean(licenseplateType),
		});
	}, [currProduct]);

	const prepareTollSectionPayload = (updatedProduct: ICartItem) => {
		handleCartItemUpdate({
			updatedCartItem: updatedProduct,
			lang,
			navigate,
			currProduct,
			setIsLoading,
			updateCart,
			updateSelectedCartItem,
			setApiError,
		});
	};

	const productSummary: IProductSummary[] = [
		{
			id: 'dates',
			label: getLabelValue('mp.valid', labels) || 'Gültig',
			value: currProduct ? (
				formatDateTimeValues(checkOutDateTime, checkInDateTime, lang, undefined, labels)
			) : (
				<Skeleton inline width="100%" height="100%" />
			),
		},
		{
			id: 'trips',
			type: correspondingTollSections && correspondingTollSections.length > 1 ? 'edit' : undefined,
			label: getLabelValue('mp.singleTrip', labels) || 'Fahrt',
			tracking: {
				label: 'Anzahl Fahrten bearbeiten',
				element: 'Warenkorb | Produktkachel',
				category: 'Content',
			},
			value: currProduct ? (
				formatTripsAmountLabel(offer.trips, labels)
			) : (
				<Skeleton inline width="100%" height="100%" />
			),
			component: (
				<TollSectionConfiguration
					simple
					route={offer}
					hasError={hasError}
					productCode={updatedProductCode}
					correspondingTollSections={correspondingTollSections}
					initSubroute={offer?.sectionName}
					setHasError={setHasError}
					setProductCode={setUpdatedProductCode}
					labels={labels}
					contentfulLabels={contentfulLabels as ITypeVignetteOrderingProcessFields}
				/>
			),
			hasErrors: hasError || !updatedProductCode,
			handleChange: () => {
				if (!updatedProductCode) {
					setHasError(true);
				} else {
					const updatedOffer = tollSectionOffers.find(
						(tollSectionOffer) => tollSectionOffer.serviceCatalogCode === updatedProductCode,
					);
					if (updatedOffer) {
						prepareTollSectionPayload({
							...currProduct,
							offerHash: updatedOffer.offerHash,
						});
					}
				}
			},
			handleClose: () => {
				setHasError(false);
				if (offer.serviceCatalogCode !== updatedProductCode) {
					setUpdatedProductCode(offer.serviceCatalogCode);
				}
			},
		},
		{
			id: 'licenseplate',
			type: 'edit',
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			tracking: {
				label: 'Kennzeichen bearbeiten',
				element: 'Warenkorb | Produktkachel',
				category: 'Content',
			},
			value: currProduct ? formatPlates(licenseplate) : <Skeleton inline height="100%" width="100%" />,
			component: (
				<EnterPlates
					withAutoFocus
					border
					insideOfModal
					hasSpecialPlates={hasSpecialPlates}
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={offer.customerCountries}
					setHideModal={setHideModal}
				/>
			),
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hiddenModal,
			handleChange: () => {
				if (!updatedLicencePlate.confirmed) return;
				if (Array.isArray(updatedLicencePlate.value)) {
					prepareTollSectionPayload({
						...currProduct,
						licenseplate: String(updatedLicencePlate.value),
						countryCode: updatedLicencePlate.countryCode,
						licenseplateType: Number(updatedLicencePlate.specialPlates),
					});
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
		deleteProductOption({
			uuid,
			offer,
			navigate,
			cartLabels,
			lang,
			labels,
			updateCart,
			redirectToEmptyCart,
			setApiError,
			updateSelectedCartItem,
			setIsLoading,
		}),
	];

	return (
		<>
			{renderProductSummaryElement(productSummary, offer)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

const FlexServiceProductSummary = ({
	currProduct,
	hasSpecialPlates,
	cartLabels,
	setIsLoading,
	redirectToEmptyCart,
}: ITollSectionSummary) => {
	const navigate = useNavigate();
	const { labels, lang } = useContext(OfferContext);
	const { contentfulLabels, updateCart, updateSelectedCartItem } = useContext(VignetteContext);
	const { offer, licenseplate, uuid, countryCode, licenseplateType } = currProduct;
	const [hiddenModal, setHideModal] = useState<boolean>(false);
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;

	useEffect(() => {
		setUpdatedLicencePlate({
			value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
			error: false,
			confirmed: false,
			countryCode,
			specialPlates: Boolean(licenseplateType),
		});
	}, [currProduct]);

	const prepareTollSectionPayload = (updatedProduct: ICartItem) => {
		handleCartItemUpdate({
			updatedCartItem: updatedProduct,
			lang,
			navigate,
			currProduct,
			useracceptance: true,
			setIsLoading,
			updateCart,
			updateSelectedCartItem,
			setApiError,
		});
	};

	const productSummary: IProductSummary[] = [
		{
			id: 'info',
			label: currentLabels?.cart?.validity || 'Gültigkeit',
			value: currentLabels?.cart?.untilFurtherNotice || 'ab sofort bis auf Widerruf',
		},
		{
			id: 'licenseplate',
			type: 'edit',
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			tracking: {
				label: 'Kennzeichen bearbeiten',
				element: `Warenkorb | Produktkachel`,
				category: 'Content',
			},
			value: currProduct ? formatPlates(licenseplate) : <Skeleton inline height="100%" width="100%" />,
			component: (
				<EnterPlates
					withAutoFocus
					border
					insideOfModal
					hasSpecialPlates={hasSpecialPlates}
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={offer.customerCountries}
					setHideModal={setHideModal}
				/>
			),
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hiddenModal,
			handleChange: () => {
				if (!updatedLicencePlate.confirmed) return;
				if (Array.isArray(updatedLicencePlate.value)) {
					prepareTollSectionPayload({
						...currProduct,
						licenseplate: String(updatedLicencePlate.value),
						countryCode: updatedLicencePlate.countryCode,
						licenseplateType: Number(updatedLicencePlate.specialPlates),
					});
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
		deleteProductOption({
			uuid,
			offer,
			navigate,
			cartLabels,
			lang,
			labels,
			updateCart,
			redirectToEmptyCart,
			setApiError,
			updateSelectedCartItem,
			setIsLoading,
		}),
	];

	return (
		<>
			{renderProductSummaryElement(productSummary, offer)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

export const ProductSummaryVariants = ({
	multipleProducts,
	cartItem,
	hasCategories,
	hasSpecialPlates = false,
	hasFuelType,
	memberNo,
	cartLabels,
	setIsLoading,
}: IProductSummaryVariants) => {
	const navigate = useNavigate();
	const { lang } = useContext(OfferContext);
	const { moduleTypeId } = cartItem.offer;
	const redirectToEmptyCart = () => {
		navigate(getUrlBasePath(`/empty-cart${setRouteLangParam(lang)}`));
	};

	const getCorrespondingProductSummaryElement = (id: number) => {
		switch (id) {
			case 2:
				return (
					<VignetteProductSummary
						multipleProducts={multipleProducts}
						currProduct={cartItem}
						hasCategories={hasCategories}
						hasSpecialPlates={hasSpecialPlates}
						hasFuelType={hasFuelType}
						memberNo={memberNo}
						cartLabels={cartLabels}
						redirectToEmptyCart={redirectToEmptyCart}
						setIsLoading={setIsLoading}
					/>
				);
			case 3:
				return (
					<TollRouteProductSummary
						currProduct={cartItem}
						hasSpecialPlates={hasSpecialPlates}
						cartLabels={cartLabels}
						redirectToEmptyCart={redirectToEmptyCart}
						setIsLoading={setIsLoading}
					/>
				);
			case 4:
				return (
					<FlexServiceProductSummary
						currProduct={cartItem}
						hasSpecialPlates={hasSpecialPlates}
						setIsLoading={setIsLoading}
						redirectToEmptyCart={redirectToEmptyCart}
					/>
				);
			default:
				return null;
		}
	};

	return getCorrespondingProductSummaryElement(moduleTypeId);
};
